import AttorneySubmissionsProvider from "@/components/DirectorySubmissions/AttorneySubmissionsProvider";
import Spinner from "@/components/Spinner";
import { useAuth } from "@/contexts/AuthHook";
import AttorneyLayout from "@/layouts/AttorneyLayout";
import AuthLayout from "@/layouts/AuthLayout";
import MainLayout from "@/layouts/MainLayout";
import MinimalLayout from "@/layouts/MinimalLayout";
import ProtectedLayout from "@/layouts/ProtectedLayout";
import Page404 from "@/pages/Page404";
import { mfaOkAtom } from "@/supabase";
import { useFeatureFlags, useIsFeatureEnabled } from "@precedent/shared-util";
import { useAtomValue } from "jotai";
import { lazy } from "react";
import { Navigate, type RouteObject, useParams } from "react-router-dom";

// Lazy load components
const SettingsPage = lazy(() => import("@/pages/Settings"));
const FirmSettingsPage = lazy(() => import("@/pages/FirmSettings"));
const ChatPage = lazy(() => import("@/pages/Chat"));
const DocumentUpload = lazy(() => import("@/pages/DocumentUpload"));
const DocumentPreview = lazy(() => import("@/pages/DocumentPreview"));
const IntappExport = lazy(() => import("@/pages/IntappExport"));
const AttorneyMattersPage = lazy(() => import("@/pages/AttorneyMattersPage"));
const AttorneyMattersPageV2 = lazy(
  () => import("@/pages/AttorneyMattersPageV2"),
);
const AttorneyReviewList = lazy(() => import("@/pages/AttorneyReviewList"));
const DirectorySubmissions = lazy(() => import("@/pages/DirectorySubmissions"));
const DirectorySubmissionsV2 = lazy(
  () => import("@/pages/DirectorySubmissionsV2"),
);
const KnowledgeGraph = lazy(() => import("@/pages/KnowledgeGraph"));
const SubmissionDetail = lazy(
  () => import("@/pages/DirectorySubmissions/SubmissionDetail"),
);
const SubmissionDetailV2 = lazy(
  () => import("@/pages/DirectorySubmissionsV2/SubmissionDetail"),
);
const SubmissionReviewsList = lazy(
  () => import("@/pages/SubmissionReviewsList"),
);
const OmniformSubmissionReviewsList = lazy(
  () => import("@/pages/OmniformSubmissionReviewsList"),
);
const AttorneyReviewPageV2 = lazy(() => import("@/pages/AttorneyReviewPageV2"));
const ChambersSubmissionHistory = lazy(
  () => import("@/pages/DirectorySubmissions/ChambersSubmissionHistory"),
);
const ChambersSubmissionHistoryV2 = lazy(
  () => import("@/pages/DirectorySubmissionsV2/ChambersSubmissionHistory"),
);
const Questionnaires = lazy(() => import("@/pages/Questionnaires"));
const QuestionnaireDetail = lazy(
  () => import("@/pages/Questionnaires/QuestionnaireDetail"),
);
const Urls = lazy(() => import("@/pages/Urls"));
const Recovery = lazy(() => import("@/pages/auth/Recovery"));
const Reset = lazy(() => import("@/pages/auth/Reset"));
const MagicLink = lazy(() => import("@/pages/auth/MagicLink"));
const SubmissionReviewMagicLink = lazy(
  () => import("@/pages/auth/SignInForSubmissionReview"),
);
const SignIn = lazy(() => import("@/pages/auth/SignIn"));
const SignUp = lazy(() => import("@/pages/auth/SignUp"));
const SignInWithMagicLink = lazy(
  () => import("@/pages/auth/SignInWithMagicLink"),
);
const AttorneyReviewPage = lazy(() => import("@/pages/AttorneyReviewPage"));
const AttorneySubmissionConfirmationPage = lazy(
  () => import("@/pages/AttorneySubmissionConfirmationPage"),
);
const Help = lazy(() => import("@/pages/Help"));
const DownloadChambersArchive = lazy(
  () => import("@/pages/DownloadChambersArchive"),
);

// Wrapper component to pass attorneyLinkId to AttorneySubmissionsProvider
const AttorneyMattersWrapper: React.FC = () => {
  const { attorneyLinkId } = useParams<{ attorneyLinkId: string }>();
  return (
    <AttorneySubmissionsProvider attorneyLinkId={attorneyLinkId}>
      <AttorneyMattersPage />
    </AttorneySubmissionsProvider>
  );
};

// Feature protection HOC
const withFeatureProtection = (
  WrappedComponent: React.ComponentType<any>,
  featureFlag: string,
): React.ReactElement => {
  const FeatureProtectedComponent: React.FC = (props) => {
    const isFeatureEnabled = useIsFeatureEnabled(featureFlag);
    return isFeatureEnabled ? (
      <WrappedComponent {...props} />
    ) : (
      <RootRedirect />
    );
  };
  return <FeatureProtectedComponent />;
};

// Root Redirect Component
const RootRedirect = () => {
  const mfaOk = useAtomValue(mfaOkAtom);

  const chatEnabled = useIsFeatureEnabled("chat-module");
  const questionnairesEnabled = useIsFeatureEnabled("questionnaires-module");
  const directorySubmissionsEnabled = useIsFeatureEnabled(
    "directory-submissions-module",
  );

  const { userId: authUserId } = useAuth();
  const { isLoading, userId: featureFlagUserId } = useFeatureFlags();

  // Check if feature flags are still loading
  if (
    isLoading ||
    chatEnabled === null ||
    questionnairesEnabled === null ||
    directorySubmissionsEnabled === null ||
    authUserId !== featureFlagUserId
  ) {
    return <Spinner />;
  }

  // Proceed with routing decision based on loaded feature flags
  if (chatEnabled && mfaOk) {
    return <Navigate to="/chat" replace />;
  }

  if (questionnairesEnabled && mfaOk) {
    return <Navigate to="/questionnaires" replace />;
  }

  if (directorySubmissionsEnabled && mfaOk) {
    return <Navigate to="/directory-submissions" replace />;
  }

  if (mfaOk) {
    return <Navigate to="/firm-settings" replace />;
  }

  return null;
};

const routes: RouteObject[] = [
  {
    path: "/",
    element: <ProtectedLayout />,
    children: [
      {
        element: <MainLayout />, // Layout for main protected routes
        children: [
          { index: true, element: <RootRedirect /> },
          {
            path: "chat",
            children: [
              {
                index: true,
                element: withFeatureProtection(ChatPage, "chat-module"),
              },
              {
                path: ":chatId",
                element: withFeatureProtection(ChatPage, "chat-module"),
              },
            ],
          },
          {
            path: "urls",
            element: <Urls />,
          },
          {
            path: "settings",
            element: <SettingsPage />,
          },
          {
            path: "firm-settings",
            element: <FirmSettingsPage />,
          },
          {
            path: "help",
            element: <Help />,
          },
          {
            path: "questionnaires",
            children: [
              {
                index: true,
                element: withFeatureProtection(
                  Questionnaires,
                  "questionnaires-module",
                ),
              },
              {
                path: ":documentId",
                element: withFeatureProtection(
                  QuestionnaireDetail,
                  "questionnaires-module",
                ),
              },
            ],
          },
          {
            path: "documents",
            children: [
              { index: true, element: <DocumentUpload /> },
              { path: "e/:embeddingId", element: <DocumentPreview /> },
              { path: "*", element: <DocumentUpload /> },
            ],
          },
          {
            path: "directory-submissions",
            children: [
              {
                index: true,
                element: withFeatureProtection(
                  DirectorySubmissions,
                  "directory-submissions-module",
                ),
              },
              {
                path: ":submissionId",
                element: withFeatureProtection(
                  SubmissionDetail,
                  "directory-submissions-module",
                ),
              },
              {
                path: ":submissionId/revisions",
                element: withFeatureProtection(
                  SubmissionDetail,
                  "directory-submissions-module",
                ),
              },
              {
                path: ":submissionId/reviews",
                element: withFeatureProtection(
                  SubmissionReviewsList,
                  "directory-submissions-module",
                ),
              },
              {
                path: ":submissionId/chambers-submission-history",
                element: withFeatureProtection(
                  ChambersSubmissionHistory,
                  "directory-submissions-module",
                ),
              },
            ],
          },
          {
            path: "directory-submissions-v2",
            children: [
              {
                index: true,
                element: withFeatureProtection(
                  DirectorySubmissionsV2,
                  "omniform",
                ),
              },
              {
                path: ":submissionId",
                element: withFeatureProtection(SubmissionDetailV2, "omniform"),
              },
              {
                path: ":submissionId/revisions",
                element: withFeatureProtection(SubmissionDetailV2, "omniform"),
              },
              {
                path: ":submissionId/reviews",
                element: withFeatureProtection(
                  OmniformSubmissionReviewsList,
                  "omniform",
                ),
              },
              {
                path: ":submissionId/chambers-submission-history",
                element: withFeatureProtection(
                  ChambersSubmissionHistoryV2,
                  "omniform",
                ),
              },
            ],
          },
          {
            path: "knowledge",
            children: [
              {
                index: true,
                element: withFeatureProtection(
                  KnowledgeGraph,
                  "knowledge-graph",
                ),
              },
              {
                path: "type/:entityTypeId",
                element: withFeatureProtection(
                  KnowledgeGraph,
                  "knowledge-graph",
                ),
                children: [
                  {
                    path: "entity/:entityId",
                    element: withFeatureProtection(
                      KnowledgeGraph,
                      "knowledge-graph",
                    ),
                  },
                ],
              },
            ],
          },
        ],
      },
      {
        element: <MinimalLayout />,
        children: [
          { path: "intapp-export", element: <IntappExport /> },
          {
            path: "download-chambers-archive/:archiveId",
            element: <DownloadChambersArchive />,
          },
        ],
      },
    ],
  },
  {
    path: "attorney/directory-submissions",
    children: [
      {
        path: "review-submissions",
        element: <AttorneyLayout />, // these pages require login
        children: [
          {
            index: true,
            element: <AttorneyReviewList />,
          },
          {
            path: ":reviewId",
            element: <AttorneyReviewPage />,
          },
        ],
      },
      {
        path: "submit-matters",
        element: <MinimalLayout />, // these pages do not require login
        children: [
          {
            path: ":attorneyLinkId",
            element: <AttorneyMattersWrapper />,
          },
          {
            path: "v2/:attorneyLinkId",
            element: <AttorneyMattersPageV2 />,
          },
          {
            path: "confirmation",
            element: <AttorneySubmissionConfirmationPage />,
          },
        ],
      },
    ],
  },
  {
    path: "attorney/omniform-submissions",
    children: [
      {
        path: "review-submissions",
        element: <AttorneyLayout />, // requires login
        children: [
          {
            index: true,
            element: <AttorneyReviewList />,
          },
          {
            path: ":reviewId",
            element: <AttorneyReviewPageV2 />,
          },
        ],
      },
      {
        path: "submit-matters",
        element: <MinimalLayout />, // these pages do not require login
        children: [
          {
            path: ":attorneyLinkId",
            element: <AttorneyMattersPageV2 />,
          },
          {
            path: "confirmation",
            element: <AttorneySubmissionConfirmationPage />,
          },
        ],
      },
    ],
  },
  {
    path: "auth",
    element: <AuthLayout />,
    children: [
      { path: "sign-in", element: <SignIn /> },
      { path: "sign-up", element: <SignUp /> },
      { path: "recovery", element: <Recovery /> },
      { path: "set-password", element: <Reset /> },
      { path: "sign-in-with-magic-link", element: <SignInWithMagicLink /> },
      { path: "magic-link", element: <MagicLink /> },
      { path: "submission-review", element: <SubmissionReviewMagicLink /> },
    ],
  },
  {
    path: "*",
    element: <Page404 />,
  },
];

export default routes;
